/**
 * Tâche 1483 (Eric) — Thèmes proposés par l'IA, puis appliqués.
 *
 * L'IA NOMME les thèmes ; le rattachement des connaissances est fait au
 * serveur, de façon déterministe (mots-clés). Rien n'est écrit avant
 * « Appliquer » : une catégorie créée par erreur se voit partout.
 *
 * Eric a demandé de pouvoir AJOUTER un thème si la proposition ne suffit
 * pas : le thème ajouté repasse par le même calcul (`recalculer`), sinon il
 * arriverait vide dans le menu du quiz — le défaut même qu'on corrige.
 */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkle, SpinnerGap, X, Plus } from "@phosphor-icons/react";

import { API_BASE as API } from "../../lib/apiBase";

export default function ProposerThemesIA({ lang, onApplique }) {
  const tr = (fr, en) => (lang === "en" ? en : fr);
  const [ouvert, setOuvert] = useState(false);
  const [chargement, setChargement] = useState(false);
  const [apercu, setApercu] = useState(null);
  const [coches, setCoches] = useState([]);
  const [nouveauNom, setNouveauNom] = useState("");
  const [nouveauxMots, setNouveauxMots] = useState("");
  const [application, setApplication] = useState(false);

  const erreur = (err, repli) => toast.error(
    err?.response?.data?.detail || repli);

  const proposer = async () => {
    setOuvert(true);
    setChargement(true);
    try {
      const { data } = await axios.post(`${API}/api/themes-ia/proposer`, {},
        { withCredentials: true });
      setApercu(data);
      setCoches(data.themes.filter((t) => t.nb_documents > 0).map((t) => t.nom));
    } catch (err) {
      erreur(err, tr("Proposition impossible", "Suggestion failed"));
      setOuvert(false);
    } finally {
      setChargement(false);
    }
  };

  const recalculer = async (themes) => {
    setChargement(true);
    try {
      const { data } = await axios.post(`${API}/api/themes-ia/recalculer`,
        { themes }, { withCredentials: true });
      setApercu({ ...data, modele: apercu?.modele });
      setCoches(data.themes.filter((t) => t.nb_documents > 0).map((t) => t.nom));
    } catch (err) {
      erreur(err, tr("Recalcul impossible", "Recompute failed"));
    } finally {
      setChargement(false);
    }
  };

  const ajouterTheme = async () => {
    const nom = nouveauNom.trim();
    if (nom.length < 2) return;
    const mots = nouveauxMots.split(",").map((m) => m.trim()).filter(Boolean);
    const themes = [...(apercu?.themes || []).map((t) => ({
      nom: t.nom, description: t.description, mots_cles: t.mots_cles,
    })), { nom, description: "", mots_cles: mots.length ? mots : [nom] }];
    setNouveauNom("");
    setNouveauxMots("");
    await recalculer(themes);
  };

  const appliquer = async () => {
    const themes = (apercu?.themes || [])
      .filter((t) => coches.includes(t.nom))
      .map((t) => ({ nom: t.nom, description: t.description, mots_cles: t.mots_cles }));
    if (themes.length === 0) return;
    setApplication(true);
    try {
      const { data } = await axios.post(`${API}/api/themes-ia/appliquer`,
        { themes }, { withCredentials: true });
      toast.success(tr(
        `${data.creees.length} catégorie(s) créée(s) · ${data.connaissances_rattachees} connaissance(s) rattachée(s)`,
        `${data.creees.length} category(ies) created · ${data.connaissances_rattachees} knowledge unit(s) attached`));
      setOuvert(false);
      setApercu(null);
      if (onApplique) await onApplique();
    } catch (err) {
      erreur(err, tr("Application impossible", "Apply failed"));
    } finally {
      setApplication(false);
    }
  };

  return (
    <>
      <button
        onClick={proposer}
        className="flex items-center gap-2 px-4 py-2.5 bg-violet-600 text-white rounded-lg text-sm font-medium hover:bg-violet-500"
        data-testid="proposer-themes-ia-btn"
      >
        <Sparkle size={16} weight="fill" /> {tr("Proposer des thèmes (IA)", "Suggest themes (AI)")}
      </button>

      {ouvert && (
        <div
          className="fixed inset-0 z-50 bg-zinc-900/60 flex items-center justify-center p-4"
          data-testid="themes-ia-modal"
        >
          <div className="bg-white rounded-xl w-full max-w-3xl max-h-[88vh] flex flex-col overflow-hidden">
            <div className="px-5 py-4 border-b border-zinc-200 flex items-start justify-between gap-4">
              <div>
                <h3 className="font-semibold text-zinc-900">
                  {tr("Thèmes proposés pour vos connaissances", "Themes suggested for your knowledge")}
                </h3>
                <p className="text-xs text-zinc-500 mt-0.5">
                  {apercu
                    ? tr(
                      `${apercu.nb_connaissances} connaissance(s) approuvée(s) · ${apercu.non_classes} sans thème. Cochez les thèmes à créer : chaque connaissance rejoint le thème dont elle cite le plus de mots-clés. Rien n'est écrit avant « Appliquer ».`,
                      `${apercu.nb_connaissances} approved knowledge unit(s) · ${apercu.non_classes} without a theme. Check the themes to create: each unit joins the theme whose keywords it cites most. Nothing is written before "Apply".`
                    )
                    : tr("L'IA analyse les titres de vos connaissances…", "The AI is analysing your knowledge titles…")}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setOuvert(false)}
                className="text-zinc-400 hover:text-zinc-700"
                data-testid="themes-ia-fermer"
              >
                <X size={18} />
              </button>
            </div>

            <div className="overflow-y-auto flex-1 p-5 space-y-2">
              {chargement && (
                <div className="flex items-center gap-2 text-sm text-zinc-500 py-8"
                     data-testid="themes-ia-chargement">
                  <SpinnerGap size={16} className="animate-spin" />
                  {tr("Calcul en cours…", "Computing…")}
                </div>
              )}
              {!chargement && (apercu?.themes || []).map((t) => (
                <label
                  key={t.nom}
                  className={`flex items-start gap-3 px-3 py-2 rounded-lg border cursor-pointer ${
                    t.nb_documents === 0
                      ? "border-zinc-100 bg-zinc-50 opacity-60"
                      : "border-zinc-200 hover:bg-zinc-50"
                  }`}
                  data-testid={`theme-ia-ligne-${t.nom}`}
                >
                  <input
                    type="checkbox"
                    checked={coches.includes(t.nom)}
                    disabled={t.nb_documents === 0}
                    onChange={() => setCoches((prev) => prev.includes(t.nom)
                      ? prev.filter((n) => n !== t.nom)
                      : [...prev, t.nom])}
                    className="mt-1 w-4 h-4 accent-violet-600"
                    data-testid={`theme-ia-case-${t.nom}`}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-zinc-900">{t.nom}</span>
                      <span className="text-[11px] text-violet-700 font-semibold">
                        {t.nb_documents === 0
                          ? tr("aucune connaissance — non créé", "no knowledge — not created")
                          : tr(`${t.nb_documents} connaissance(s)`, `${t.nb_documents} knowledge unit(s)`)}
                      </span>
                    </div>
                    {t.mots_cles.length > 0 && (
                      <p className="text-[11px] text-zinc-500 mt-0.5">
                        {tr("mots-clés : ", "keywords: ")}{t.mots_cles.join(", ")}
                      </p>
                    )}
                    {t.exemples.length > 0 && (
                      <p className="text-[11px] text-zinc-400 mt-0.5 truncate">
                        {t.exemples.join(" · ")}
                      </p>
                    )}
                  </div>
                </label>
              ))}
              {!chargement && apercu && (
                <div className="mt-4 border-t border-zinc-200 pt-4">
                  <p className="text-xs font-semibold text-zinc-700 mb-2">
                    {tr("Ajouter un thème manquant", "Add a missing theme")}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <input
                      value={nouveauNom}
                      onChange={(e) => setNouveauNom(e.target.value)}
                      placeholder={tr("Nom du thème", "Theme name")}
                      className="px-3 py-2 border border-zinc-300 rounded text-sm"
                      data-testid="theme-ia-nouveau-nom"
                    />
                    <input
                      value={nouveauxMots}
                      onChange={(e) => setNouveauxMots(e.target.value)}
                      placeholder={tr("mots-clés séparés par des virgules", "comma-separated keywords")}
                      className="flex-1 min-w-[220px] px-3 py-2 border border-zinc-300 rounded text-sm"
                      data-testid="theme-ia-nouveaux-mots"
                    />
                    <button
                      type="button"
                      onClick={ajouterTheme}
                      disabled={nouveauNom.trim().length < 2}
                      className="inline-flex items-center gap-1 px-3 py-2 rounded bg-zinc-900 text-white text-xs font-semibold disabled:opacity-40"
                      data-testid="theme-ia-ajouter-btn"
                    >
                      <Plus size={13} weight="bold" />
                      {tr("Ajouter et recalculer", "Add and recompute")}
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="px-5 py-4 border-t border-zinc-200 flex items-center justify-between gap-3">
              <span className="text-xs text-zinc-500" data-testid="themes-ia-modele">
                {apercu?.modele ? `${tr("modèle", "model")} : ${apercu.modele}` : ""}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setOuvert(false)}
                  className="px-3 py-2 text-sm text-zinc-600 hover:text-zinc-900"
                  data-testid="themes-ia-annuler"
                >
                  {tr("Annuler", "Cancel")}
                </button>
                <button
                  type="button"
                  onClick={appliquer}
                  disabled={coches.length === 0 || application || chargement}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-violet-600 text-white text-sm font-semibold disabled:opacity-40"
                  data-testid="themes-ia-appliquer"
                >
                  {application && <SpinnerGap size={14} className="animate-spin" />}
                  {tr(`Appliquer ${coches.length} thème(s)`, `Apply ${coches.length} theme(s)`)}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
